import React, { useState, useContext, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import './SettingsView.css';
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import AuthContext from "../../AuthContext";
import { gql, useMutation, useQuery } from "@apollo/client";

// GraphQL mutation to change password
const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($sessionId: String!, $oldPassword: String!, $newPassword: String!) {
    changePassword(sessionId: $sessionId, oldPassword: $oldPassword, newPassword: $newPassword) {
      success
      message
    }
  }
`;

// GraphQL mutation to update user privacy preferences
const UPDATE_PRIVACY_PREFERENCES = gql`
  mutation UpdatePrivacyPreferences(
    $sessionId: String!,
    $privacyStatus: String!,
    $storeData: Boolean!,
    $allowModel: Boolean!
  ) {
    updatePrivacyPreferences(
      sessionId: $sessionId,
      privacyStatus: $privacyStatus,
      storeData: $storeData,
      allowModel: $allowModel
    ) {
      success
      message
    }
  }
`;

// GraphQL query to get user privacy preferences
const GET_PRIVACY_PREFERENCES = gql`
  query GetPrivacyPreferences($sessionId: String!) {
    getPrivacyPreferences(sessionId: $sessionId) {
      privacyStatus
      storeData
      allowModel
    }
  }
`;

// GraphQL mutation to delete account
const DELETE_ACCOUNT_MUTATION = gql`
  mutation DeleteAccount($sessionId: String!, $password: String!) {
    deleteAccount(sessionId: $sessionId, password: $password) {
      success
      message
    }
  }
`;

const SettingsView = () => {
  const { t, i18n } = useTranslation();
  const { sessionId, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // State management
  const [selectedOption, setSelectedOption] = useState(null);
  const [privacyStatus, setPrivacyStatus] = useState('private');
  const [storeData, setStoreData] = useState(false);
  const [allowModel, setAllowModel] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Delete account state
  const [deleteStage, setDeleteStage] = useState(0); // 0: not shown, 1: initial warning, 2: final confirmation
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // Status messages states
  const [privacySaved, setPrivacySaved] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [languageSaved, setLanguageSaved] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [loadingPrivacySettings, setLoadingPrivacySettings] = useState(true);
  const [privacyError, setPrivacyError] = useState('');

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' }
  ];

  const settings = [
    {
      id: 'privacy',
      title: t('settings.privacy.title')
    },
    {
      id: 'password',
      title: t('settings.password.title')
    },
    {
      id: 'language',
      title: t('settings.language.title')
    }
  ];

  // Query to get user privacy preferences
  const { data: privacyData } = useQuery(GET_PRIVACY_PREFERENCES, {
    variables: { sessionId },
    fetchPolicy: 'network-only',
    skip: !sessionId,
    onCompleted: (data) => {
      if (data && data.getPrivacyPreferences) {
        setPrivacyStatus(data.getPrivacyPreferences.privacyStatus || 'private');
        setStoreData(data.getPrivacyPreferences.storeData || false);
        setAllowModel(data.getPrivacyPreferences.allowModel || false);
      }
      setLoadingPrivacySettings(false);
    },
    onError: () => {
      setLoadingPrivacySettings(false);
    }
  });

  // Mutation to update privacy preferences
  const [updatePrivacyPreferences, { loading: updatingPrivacy }] = useMutation(
    UPDATE_PRIVACY_PREFERENCES,
    {
      onError: (error) => {
        setPrivacyError(error.message || "Error updating privacy settings");
      }
    }
  );

  // Mutation to change password
  const [changePassword, { loading: changingPassword }] = useMutation(
    CHANGE_PASSWORD_MUTATION,
    {
      onError: (error) => {
        setPasswordError(error.message || "Network error while changing password");
      }
    }
  );

  // Mutation to delete account
  const [deleteAccount, { loading: deletingAccount }] = useMutation(
    DELETE_ACCOUNT_MUTATION,
    {
      onError: (error) => {
        setDeleteError(error.message || "Network error while deleting account");
      }
    }
  );

  // Load initial privacy settings
  useEffect(() => {
    if (privacyData && privacyData.getPrivacyPreferences) {
      const { privacyStatus: status, storeData: store, allowModel: allow } = privacyData.getPrivacyPreferences;
      setPrivacyStatus(status);
      setStoreData(store);
      setAllowModel(allow);
    }
  }, [privacyData]);

  const handleSettingClick = (id) => {
    setSelectedOption(selectedOption === id ? null : id);
    setShowDropdown(false);
    setDeleteStage(0); // Reset delete account flow when changing sections
    setDeleteError('');
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
    setPasswordError('');
  };

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    setShowDropdown(false);
  };

  const handlePrivacySave = async () => {
    try {
      // Clear previous errors
      setPrivacyError('');

      // Validate sessionId exists and is not empty
      if (!sessionId || sessionId.trim() === '') {
        setPrivacyError('Missing session ID. Please log in again.');
        return;
      }

      // Ensure storeData and allowModel are boolean values
      const boolStoreData = Boolean(storeData);
      const boolAllowModel = Boolean(allowModel);

      // Execute the mutation with properly formatted variables
      const { data } = await updatePrivacyPreferences({
        variables: {
          sessionId,
          privacyStatus,
          storeData: boolStoreData,
          allowModel: boolAllowModel
        },
        context: {
          // This helps ensure Apollo sends the data in the expected format
          headers: {
            'Content-Type': 'application/json'
          }
        }
      });
  
      // Verify the response
      if (data?.updatePrivacyPreferences) {
        if (data.updatePrivacyPreferences.success) {
          setPrivacySaved(true);
          setTimeout(() => setPrivacySaved(false), 3000);
        } else {
          setPrivacyError(data.updatePrivacyPreferences.message || 'Error updating preferences');
        }
      } else {
        setPrivacyError('Unexpected server response');
      }
    } catch (error) {
      setPrivacyError('Error saving preferences. Please try again.');
    }
  };

  const handleLanguageSave = () => {
    setLanguageSaved(true);
    setTimeout(() => setLanguageSaved(false), 3000);
  };

  // Logic to change password
  const handlePasswordSave = async () => {
    // Clear previous errors
    setPasswordError('');

    // Validate input
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError("All password fields are required");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirmation do not match");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    try {
      // Call mutation
      const { data } = await changePassword({
        variables: {
          sessionId, 
          oldPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        },
        context: {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      });

      if (!data?.changePassword) {
        setPasswordError("No response from server");
        return;
      }

      if (!data.changePassword.success) {
        setPasswordError(data.changePassword.message || "Could not change password");
        return;
      }

      // Success handling
      setPasswordSaved(true);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setPasswordSaved(false), 3000);

    } catch (error) {
      setPasswordError(error.message || "An error occurred");
    }
  };

  // Handle deleting account - first confirmation
  const handleInitiateDelete = () => {
    setDeleteStage(1);
    setDeleteError('');
  };

  // Move to final delete stage
  const handleMoveToFinalDelete = () => {
    setDeleteStage(2);
    setDeleteError('');
  };

  // Cancel delete process
  const handleCancelDelete = () => {
    setDeleteStage(0);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  // Handle final account deletion
  const handleDeleteAccount = async () => {
    // Reset previous error
    setDeleteError('');

    // Validate confirmation text
    if (deleteConfirmText !== "DELETE") {
      setDeleteError("Please type DELETE to confirm");
      return;
    }

    try {
      const { data } = await deleteAccount({
        variables: {
          sessionId,
          password: "not-used" // We're not verifying password in backend anymore
        },
        context: {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      });

      if (!data?.deleteAccount) {
        setDeleteError("No response from server");
        return;
      }

      if (!data.deleteAccount.success) {
        setDeleteError(data.deleteAccount.message || "Could not delete account");
        return;
      }

      // On successful deletion, log out and redirect to home page
      logout();
      navigate('/');
      
    } catch (error) {
      setDeleteError(error.message || "An error occurred while deleting account");
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>{t('settings.title')}</h1>
        <p>{t('settings.description')}</p>
      </div>

      <div className="settings-list">
        {settings.map((setting) => (
          <div key={setting.id} className="settings-item-container">
            <button
              className="settings-item"
              onClick={() => handleSettingClick(setting.id)}
            >
              <span>{setting.title}</span>
              <ChevronRight className="chevron-icon" />
            </button>

            {selectedOption === 'privacy' && setting.id === 'privacy' && (
              <div className="privacy-panel-new">
                <h2 className="privacy-title">{t('settings.privacy.title')}</h2>

                {loadingPrivacySettings ? (
                  <div className="loading-indicator">Loading preferences...</div>
                ) : (
                  <>
                    {/* Public/Private Section */}
                    <div className="public-private-section">
                      <h3>{t('settings.privacy.accountVisibilityTitle')}</h3>
                      <p>{t('settings.privacy.accountVisibilityDescription')}</p>
                      <div className="toggle-pills">
                        <div
                          className={`pill ${privacyStatus === 'public' ? 'selected' : ''}`}
                          onClick={() => setPrivacyStatus('public')}
                        >
                          {t('settings.privacy.public')}
                        </div>
                        <div
                          className={`pill ${privacyStatus === 'private' ? 'selected' : ''}`}
                          onClick={() => setPrivacyStatus('private')}
                        >
                          {t('settings.privacy.private')}
                        </div>
                      </div>
                    </div>

                    {/* Store prompts Section */}
                    <div className="privacy-section">
                      <h3>{t('settings.privacy.storeDataTitle')}</h3>
                      <p>{t('settings.privacy.storeDataDescription')}</p>
                      <div className="toggle-pills">
                        <div
                          className={`pill ${storeData ? 'selected' : ''}`}
                          onClick={() => setStoreData(true)}
                        >
                          {t('general.yes') || "Yes"}
                        </div>
                        <div
                          className={`pill ${!storeData ? 'selected' : ''}`}
                          onClick={() => setStoreData(false)}
                        >
                          {t('general.no') || "No"}
                        </div>
                      </div>
                    </div>

                    {/* Allow data Section */}
                    <div className="privacy-section">
                      <h3>{t('settings.privacy.allowModelTitle')}</h3>
                      <p>{t('settings.privacy.allowModelDescription')}</p>
                      <div className="toggle-pills">
                        <div
                          className={`pill ${allowModel ? 'selected' : ''}`}
                          onClick={() => setAllowModel(true)}
                        >
                          {t('general.yes') || "Yes"}
                        </div>
                        <div
                          className={`pill ${!allowModel ? 'selected' : ''}`}
                          onClick={() => setAllowModel(false)}
                        >
                          {t('general.no') || "No"}
                        </div>
                      </div>
                    </div>

                    {/* Delete Account Section */}
                    <div className="delete-account-section">
                      <h3 className="delete-account-title">{t('settings.privacy.deleteDataTitle')}</h3>
                      <p className="delete-account-description">{t('settings.privacy.deleteDataDescription')}</p>
                      
                      {deleteStage === 0 && (
  <button 
  className="delete-button delete-account-button"
  onClick={handleInitiateDelete}
>
  {t('settings.privacy.deleteDataButton')}
</button>
                      )}

                      {deleteStage === 1 && (
                        <div className="delete-warning-container">
                          <div className="delete-warning-header">
                            <i className="warning-icon">⚠️</i>
                            <h4>{t('settings.privacy.warningTitle')}</h4>
                          </div>
                          <p className="delete-warning-message">
                            {t('settings.privacy.warningDescription')}
                          </p>
                          <div className="delete-actions">
                            <button 
                              className="delete-cancel-button"
                              onClick={handleCancelDelete}
                            >
                              {t('settings.privacy.cancel')}
                            </button>
                            <button 
                              className="delete-proceed-button"
                              onClick={handleMoveToFinalDelete}
                            >
                              {t('settings.privacy.proceed')}
                            </button>
                          </div>
                        </div>
                      )}

                      {deleteStage === 2 && (
                        <div className="delete-final-container">
                          <div className="delete-warning-header">
                            <i className="warning-icon">⚠️</i>
                            <h4>{t('settings.privacy.finalWarningTitle')}</h4>
                          </div>
                          <p className="delete-warning-message">
                            {t('settings.privacy.finalWarningDescription')}
                          </p>
                          <div className="delete-confirm-input-container">
                            <label htmlFor="delete-confirm-input">
                              {t('settings.privacy.typeToConfirm', { text: 'DELETE' })}
                            </label>
                            <input
                              id="delete-confirm-input"
                              type="text"
                              value={deleteConfirmText}
                              onChange={(e) => setDeleteConfirmText(e.target.value)}
                              placeholder="DELETE"
                              className="delete-confirm-input"
                            />
                          </div>

                          {deleteError && (
                            <div className="delete-error-message">
                              {deleteError}
                            </div>
                          )}

                          <div className="delete-actions">
                            <button 
                              className="delete-cancel-button"
                              onClick={handleCancelDelete}
                            >
                              {t('settings.privacy.cancel')}
                            </button>
                            <button 
                              className="delete-final-button"
                              onClick={handleDeleteAccount}
                              disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                            >
                              {deletingAccount 
                                ? t('settings.privacy.deleting') || "Deleting..." 
                                : t('settings.privacy.confirmDelete') || "Delete My Account"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Error message display */}
                    {privacyError && (
                      <div className="error-message">
                        {privacyError}
                      </div>
                    )}

                    {/* Save button and confirmation message */}
                    <div className="button-container">
                      <button 
                        className="save-button" 
                        onClick={handlePrivacySave}
                        disabled={updatingPrivacy}
                      >
                        {updatingPrivacy 
                          ? t('profilePage.modal.saving') || "Saving..." 
                          : t('settings.privacy.save') || "Save"}
                      </button>
                    </div>
                    {privacySaved && (
                      <div className="save-feedback">
                        <span className="checkmark">✓</span>
                        <span>{t('settings.privacy.savedMessage')}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* PASSWORD */}
            {selectedOption === 'password' && setting.id === 'password' && (
              <div className="password-panel">
                <p className="password-requirements">
                  {t('settings.password.requirements')}
                </p>

                <div className="password-form">
                  <div className="inputs-container">
                    <input
                      type="password"
                      name="currentPassword"
                      placeholder={t('settings.password.currentPassword')}
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      disabled={changingPassword}
                    />
                    <input
                      type="password"
                      name="newPassword"
                      placeholder={t('settings.password.newPassword')}
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      disabled={changingPassword}
                    />
                    <input
                      type="password"
                      name="confirmPassword"
                      placeholder={t('settings.password.retypePassword')}
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      disabled={changingPassword}
                    />
                  </div>

                  {/* Error message display */}
                  {passwordError && (
                    <div className="error-message">
                      {passwordError}
                    </div>
                  )}

                  <NavLink to="/ForgotPassword" className="forgot-password">
                    {t('settings.password.forgot')}
                  </NavLink>

                  <button
                    className="change-password-button"
                    onClick={handlePasswordSave}
                    disabled={changingPassword}
                  >
                    {changingPassword ? "Changing..." : t('settings.password.change')}
                  </button>
                </div>

                {passwordSaved && (
                  <div className="save-feedback">
                    <span className="checkmark">✓</span>
                    <span>{t('settings.password.savedMessage')}</span>
                  </div>
                )}
              </div>
            )}

            {/* LANGUAGE */}
            {selectedOption === 'language' && setting.id === 'language' && (
              <div className="language-panel">
                <h2 className="language-title">{t('settings.language.preferred')}</h2>
                <div className="language-selection">
                  <div className="language-input">
                    <span className="language-text">
                      {i18n.language === 'en' ? 'English' : 'Español'}
                    </span>
                    <button
                      className="edit-language-button"
                      onClick={() => setShowDropdown((prev) => !prev)}
                    >
                      ✏️
                    </button>
                    {showDropdown && (
                      <div className="language-dropdown">
                        {languages.map((lang) => (
                          <div
                            key={lang.code}
                            className="language-option"
                            onClick={() => handleLanguageChange(lang.code)}
                          >
                            {lang.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    className="save-language-button"
                    onClick={handleLanguageSave}
                  >
                    {t('settings.language.save')}
                  </button>
                </div>
                {languageSaved && (
                  <div className="save-feedback">
                    <span className="checkmark">✓</span>
                    <span>{t('settings.language.savedMessage')}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SettingsView;